import { ArrowLeftOutlined } from '@ant-design/icons';
import { EnumCambiamentoStatoRequestTransizione } from '@framework/api-client';
import { useCurrentUser } from '@framework/auth';
import { AiModuleCheckMessage } from '@framework/common-web';
import {
  IModuleStateMachineRecord,
  ModuleStateMachine,
  SubmissionModuleState,
} from '@framework/frontoffice-submission';
import { useTranslation } from '@framework/i18n';
import { AsyncButton } from '@framework/ui';
import { Flex } from 'antd';
import React, { useMemo } from 'react';
import { ActionButtonContainer } from './ActionButtonContainer';
import { ActionMessage } from './ActionMessage';
import { useSubmissionModuleStateChanger } from './useSubmissionModuleStateChanger';

export interface IBackToCompilationButtonProps {
  data: IModuleStateMachineRecord;
  extra?: React.ReactNode;
}

export function BackToCompilationButton(props: IBackToCompilationButtonProps) {
  const { data, extra } = props;

  const currentUser = useCurrentUser();
  const { t } = useTranslation();

  const canReturnToCompilation = useMemo(() => {
    return ModuleStateMachine.canTransition(data, {
      from: SubmissionModuleState.Completed,
      to: SubmissionModuleState.Compilation,
      user: currentUser,
    });
  }, [currentUser, data]);

  const { handleClick } = useSubmissionModuleStateChanger({
    data,
    successMessage: t(
      'submission-action-button.modulo-nuovamente-compilabile',
      'Modulo nuovamente compilabile',
    ),
  });

  const isSignable = useMemo(() => {
    return ModuleStateMachine.can(data, '.signable', currentUser).allowed;
  }, [currentUser, data]);

  const statusMessage = isSignable
    ? t(
        'submission-action-button.modulo-compilato-e-firmato-precedentemente',
        'Il modulo è già stato compilato e firmato.',
      )
    : t(
        'submission-action-button.modulo-compilato-precedentemente',
        'Il modulo è già stato compilato.',
      );

  if (
    !canReturnToCompilation.allowed &&
    canReturnToCompilation.message === 'is-not-manager'
  ) {
    return <ActionMessage message={statusMessage} type="info" />;
  }

  if (!canReturnToCompilation.allowed) return null;

  return (
    <>
      <ActionButtonContainer
        message={
          <ActionMessage
            message={
              isSignable
                ? t(
                    'submission-action-button.modulo-compilato-e-firmato',
                    'Il modulo è stato compilato e firmato. Riportando il modulo in compilazione, sarà necessario firmarlo nuovamente.',
                  )
                : t(
                    'submission-action-button.modulo-compilato',
                    'Il modulo è stato compilato.',
                  )
            }
            type="info"
          />
        }
        button={
          <Flex vertical>
            <AsyncButton
              block
              size="large"
              onClick={() =>
                handleClick(
                  EnumCambiamentoStatoRequestTransizione.RIPORTA_IN_COMPILAZIONE,
                )
              }
            >
              <ArrowLeftOutlined aria-hidden="true" />{' '}
              {t(
                'submission-action-button.ritorna-in-compilazione',
                'Ritorna in compilazione',
              )}
            </AsyncButton>
          </Flex>
        }
      />
      <AiModuleCheckMessage
        result={data.module?.verificaFirmaAutografaAi}
        isDigitallySigned={data.module?.flagDigitalSignature}
        isBackoffice={false}
      />
    </>
  );
}
